// Firebase configuration and Phone Auth (send OTP on client, verify, get idToken for backend).
// Env vars: REACT_APP_FIREBASE_API_KEY, REACT_APP_FIREBASE_AUTH_DOMAIN, REACT_APP_FIREBASE_PROJECT_ID,
// REACT_APP_FIREBASE_STORAGE_BUCKET, REACT_APP_FIREBASE_MESSAGING_SENDER_ID, REACT_APP_FIREBASE_APP_ID, REACT_APP_FIREBASE_MEASUREMENT_ID (optional).
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || undefined,
};

// reCAPTCHA Enterprise site key for Identity Platform
if (process.env.REACT_APP_FIREBASE_RECAPTCHA_SITE_KEY) {
  window.FIREBASE_RECAPTCHA_SITE_KEY = process.env.REACT_APP_FIREBASE_RECAPTCHA_SITE_KEY;
}

// Initialize once (singleton)
let firebaseAppInstance = null;
function getFirebaseApp() {
  if (!firebaseAppInstance) {
    firebaseAppInstance = initializeApp(firebaseConfig);
  }
  return firebaseAppInstance;
}
export const firebaseApp = getFirebaseApp();

function isFirebaseConfigured() {
  return !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  );
}

const isDev = () =>
  typeof process !== "undefined" &&
  process.env?.NODE_ENV === "development";

function devLog(...args) {
  if (isDev()) {
    console.log("[firebase]", ...args);
  }
}

/** Map Firebase auth errors to user-friendly messages (no raw JSON). */
function toFriendlyError(codeOrMessage) {
  const code = typeof codeOrMessage === "string" ? codeOrMessage : "";
  const map = {
    "auth/invalid-phone-number": "Please enter a valid phone number.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
    "auth/quota-exceeded": "SMS limit reached. Please try again later.",
    "auth/captcha-check-failed": "Verification was blocked. Please disable blockers and try again.",
    "auth/invalid-verification-code": "Invalid or expired code. Please try again.",
    "auth/session-expired": "Session expired. Please request a new code.",
    "auth/network-request-failed": "Network error. Please check your connection.",
    "auth/popup-blocked": "Verification was blocked. Please allow popups and try again.",
    "auth/argument-error": "Verification setup failed. Please refresh and try again.",
  };
  if (map[code]) return map[code];
  if (code.startsWith("auth/")) return "Verification failed. Please try again.";
  if (typeof codeOrMessage === "string" && codeOrMessage.length < 120) return codeOrMessage;
  return "Something went wrong. Please try again.";
}

let recaptchaVerifier = null;
const RECAPTCHA_CONTAINER_ID = "recaptcha-container";

/**
 * Reset reCAPTCHA so it can be set up again (e.g. after Back to Form or between attempts).
 * Call before setupRecaptcha when re-initializing.
 */
export function resetRecaptcha() {
  const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (container) {
    container.innerHTML = "";
  }
  recaptchaVerifier = null;
  devLog("Recaptcha reset");
}

/**
 * Setup reCAPTCHA verifier (invisible) attached to #recaptcha-container.
 * Ensure the DOM element exists before calling (e.g. in SignupSimple step 1).
 * Call resetRecaptcha() first when re-initializing (e.g. after Back to Form).
 * @param {string} containerId - DOM element id (default: recaptcha-container)
 */
export const setupRecaptcha = (containerId = RECAPTCHA_CONTAINER_ID) => {
  if (!isFirebaseConfigured()) return null;
  const container = document.getElementById(containerId);
  if (!container) {
    devLog("setupRecaptcha: container not found", containerId);
    return null;
  }
  try {
    if (recaptchaVerifier) {
      container.innerHTML = "";
      recaptchaVerifier = null;
    }
    const auth = getAuth(firebaseApp);
    recaptchaVerifier = new RecaptchaVerifier(auth, container, {
      size: "invisible",
      callback: () => {},
      "expired-callback": () => {},
    });
    devLog("RecaptchaVerifier ready (invisible)");
    return recaptchaVerifier;
  } catch (e) {
    if (isDev()) console.error("[firebase] setupRecaptcha error:", e?.message);
    return null;
  }
};

/**
 * Send OTP via Firebase Phone Auth. Returns confirmationResult for verifyOTP.
 * In dev when Firebase is not configured, returns a mock result so verifyOTP can return dev-bypass token.
 * @param {string} phone - E.164 phone (e.g. +919876543210)
 * @returns {{ success: boolean, confirmationResult?: object, error?: string }}
 */
export const sendOTP = async (phone) => {
  if (!isFirebaseConfigured()) {
    if (isDev()) {
      devLog("Dev: Firebase not configured; mock OTP sent for", phone?.replace?.(/(.{3}).*(.{4})/, "$1***$2"));
      return { success: true, confirmationResult: { __devMock: true } };
    }
    return { success: false, error: "OTP service unavailable." };
  }
  if (!phone || !phone.startsWith("+")) {
    return { success: false, error: "Please enter a valid phone number." };
  }
  try {
    const auth = getAuth(firebaseApp);
    if (!recaptchaVerifier) {
      return { success: false, error: "Verification not ready. Please refresh and try again." };
    }
    const confirmationResult = await signInWithPhoneNumber(auth, phone, recaptchaVerifier);
    devLog("sendOTP success for", phone?.replace?.(/(.{3}).*(.{4})/, "$1***$2"));
    return { success: true, confirmationResult };
  } catch (e) {
    devLog("sendOTP error", e?.code, e?.message);
    const message = toFriendlyError(e?.code || e?.message);
    return { success: false, error: message };
  }
};

/**
 * Verify OTP and return Firebase ID token for backend signup.
 * In dev with mock confirmationResult, returns idToken "dev-bypass" so backend accepts signup.
 * @param {object} confirmationResult - From sendOTP()
 * @param {string} code - 6-digit OTP
 * @returns {{ success: boolean, idToken?: string, error?: string }}
 */
export const verifyOTP = async (confirmationResult, code) => {
  if (confirmationResult?.__devMock && isDev()) {
    devLog("Dev: mock verify OTP accepted");
    return { success: true, idToken: "dev-bypass" };
  }
  if (!confirmationResult || !confirmationResult.confirm) {
    return { success: false, error: "Verification expired. Please request a new code." };
  }
  if (!code || String(code).length !== 6) {
    return { success: false, error: "Please enter a valid 6-digit code." };
  }
  try {
    const userCredential = await confirmationResult.confirm(code);
    const idToken = await userCredential.user.getIdToken();
    devLog("verifyOTP success");
    return { success: true, idToken };
  } catch (e) {
    devLog("verifyOTP error", e?.code, e?.message);
    const message = toFriendlyError(e?.code || e?.message);
    return { success: false, error: message };
  }
};

/**
 * Sign in with Google popup. Returns Firebase ID token for backend.
 * @returns {{ success: boolean, idToken?: string, email?: string, displayName?: string, error?: string }}
 */
export const signInWithGoogle = async () => {
  if (!isFirebaseConfigured()) {
    return { success: false, error: "Google sign-in unavailable." };
  }
  try {
    const auth = getAuth(firebaseApp);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const result = await signInWithPopup(auth, provider);
    const idToken = await result.user.getIdToken();
    devLog("Google sign-in success:", result.user.email);
    return {
      success: true,
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
    };
  } catch (e) {
    devLog("Google sign-in error", e?.code, e?.message);
    const message = toFriendlyError(e?.code || e?.message);
    return { success: false, error: message };
  }
};

export const requestNotificationPermission = async () => null;
export const onMessageListener = async () => null;
